// Procedurally generated backgrounds for social share cards.
// Everything is drawn in code (no bundled artwork), so there are no licensing
// constraints and the library costs ~0 bytes of assets. Each background is
// deterministic: the same id always renders the same image.

// Small seeded PRNG so jagged mountains/speckles are stable per background.
function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a |= 0; a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashSeed(str) {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

function linear(ctx, w, h, stops, { angle = 90 } = {}) {
  const rad = (angle * Math.PI) / 180;
  const x = Math.cos(rad), y = Math.sin(rad);
  const cx = w / 2, cy = h / 2;
  const len = (Math.abs(w * x) + Math.abs(h * y)) / 2;
  const g = ctx.createLinearGradient(cx - x * len, cy - y * len, cx + x * len, cy + y * len);
  const n = stops.length - 1;
  stops.forEach((c, i) => g.addColorStop(i / n, c));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

function radial(ctx, w, h, stops, { fx = 0.5, fy = 0.4 } = {}) {
  const r = Math.max(w, h) * 0.9;
  const g = ctx.createRadialGradient(w * fx, h * fy, 0, w * fx, h * fy, r);
  const n = stops.length - 1;
  stops.forEach((c, i) => g.addColorStop(i / n, c));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
}

// ---- Nature scenes (stylized flat-illustration silhouettes) ----

function drawMountains(ctx, w, h, { sky, sun, layers, snow, moon, stars }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  if (stars) drawStars(ctx, w, h, rng, { count: 90, maxY: 0.42 });
  if (moon) drawMoon(ctx, w, h, moon);
  if (sun) {
    ctx.fillStyle = sun.color;
    ctx.beginPath();
    ctx.arc(w * sun.x, h * sun.y, w * sun.r, 0, Math.PI * 2);
    ctx.fill();
  }
  layers.forEach((color, li) => {
    const baseY = h * (0.45 + li * 0.16);
    const jag = h * (0.10 - li * 0.02);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(0, baseY + (rng() - 0.5) * jag);
    const peaks = 4 + li;
    const peakPoints = [];
    for (let i = 1; i <= peaks; i++) {
      const px = (w / peaks) * i;
      const py = baseY + (rng() - 0.5) * 2 * jag - (i % 2 ? jag : 0);
      ctx.lineTo(px - w / peaks / 2, py);
      if (i % 2) peakPoints.push([px - w / peaks / 2, py]);
      ctx.lineTo(px, baseY + (rng() - 0.5) * jag);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
    // Snow caps on the back (furthest, most distant) layer's peaks only —
    // real ranges only show snow on their highest, farthest summits.
    if (snow && li === 0) {
      ctx.fillStyle = 'rgba(255,255,255,0.92)';
      peakPoints.forEach(([px, py]) => {
        const capW = (w / peaks) * 0.5;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.lineTo(px - capW * 0.4, py + capW * 0.32);
        ctx.lineTo(px + capW * 0.4, py + capW * 0.32);
        ctx.closePath();
        ctx.fill();
      });
    }
  });
}

function drawOcean(ctx, w, h, { sky, sea, sun }, rng) {
  const horizon = h * 0.52;
  linear(ctx, w, horizon, sky, { angle: 90 });
  ctx.save();
  ctx.translate(0, horizon);
  linear(ctx, w, h - horizon, sea, { angle: 90 });
  // light streaks on the water
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 24; i++) {
    const y = rng() * (h - horizon);
    const len = w * (0.05 + rng() * 0.2);
    ctx.fillRect(w / 2 - len / 2 + (rng() - 0.5) * w * 0.5, y, len, 2 + rng() * 3);
  }
  ctx.restore();
  if (sun) {
    ctx.fillStyle = sun.color;
    ctx.beginPath();
    ctx.arc(w * 0.5, horizon - w * sun.r * 0.6, w * sun.r, 0, Math.PI * 2);
    ctx.fill();
    ctx.globalAlpha = 0.35;
    ctx.fillRect(w * 0.5 - w * sun.r * 0.5, horizon, w * sun.r, h - horizon);
    ctx.globalAlpha = 1;
  }
}

function drawBeach(ctx, w, h, { sky, sea, sand }) {
  linear(ctx, w, h, sky, { angle: 90 });
  const seaTop = h * 0.5, sandTop = h * 0.72;
  ctx.fillStyle = sea;
  ctx.fillRect(0, seaTop, w, h - seaTop);
  // sand with a soft wave edge
  ctx.fillStyle = sand;
  ctx.beginPath();
  ctx.moveTo(0, h);
  ctx.lineTo(0, sandTop + h * 0.03);
  ctx.bezierCurveTo(w * 0.3, sandTop - h * 0.03, w * 0.7, sandTop + h * 0.06, w, sandTop);
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fill();
  // foam line
  ctx.strokeStyle = 'rgba(255,255,255,0.7)';
  ctx.lineWidth = h * 0.006;
  ctx.beginPath();
  ctx.moveTo(0, sandTop + h * 0.03);
  ctx.bezierCurveTo(w * 0.3, sandTop - h * 0.03, w * 0.7, sandTop + h * 0.06, w, sandTop);
  ctx.stroke();
}

function drawRiver(ctx, w, h, { sky, hills, river }) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.5;
  ctx.fillStyle = hills[0];
  ctx.beginPath();
  ctx.moveTo(0, h); ctx.lineTo(0, horizon);
  ctx.quadraticCurveTo(w * 0.35, horizon - h * 0.1, w * 0.55, horizon);
  ctx.lineTo(w * 0.55, h);
  ctx.closePath(); ctx.fill();
  ctx.fillStyle = hills[1];
  ctx.beginPath();
  ctx.moveTo(w, h); ctx.lineTo(w, horizon);
  ctx.quadraticCurveTo(w * 0.7, horizon - h * 0.08, w * 0.45, horizon);
  ctx.lineTo(w * 0.45, h);
  ctx.closePath(); ctx.fill();
  // river widening toward the viewer
  const g = ctx.createLinearGradient(0, horizon, 0, h);
  g.addColorStop(0, river[0]);
  g.addColorStop(1, river[1]);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(w * 0.47, horizon);
  ctx.bezierCurveTo(w * 0.42, h * 0.65, w * 0.2, h * 0.8, w * 0.05, h);
  ctx.lineTo(w * 0.95, h);
  ctx.bezierCurveTo(w * 0.72, h * 0.78, w * 0.55, h * 0.64, w * 0.53, horizon);
  ctx.closePath();
  ctx.fill();
}

function drawRainforest(ctx, w, h, { sky, canopy }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  canopy.forEach((color, li) => {
    const baseY = h * (0.35 + li * 0.18);
    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, h);
    ctx.lineTo(0, baseY);
    const bumps = 6 + li * 2;
    for (let i = 0; i < bumps; i++) {
      const x1 = (w / bumps) * (i + 0.5);
      const x2 = (w / bumps) * (i + 1);
      const lift = h * (0.04 + rng() * 0.05);
      ctx.quadraticCurveTo(x1, baseY - lift, x2, baseY + (rng() - 0.5) * h * 0.02);
    }
    ctx.lineTo(w, h);
    ctx.closePath();
    ctx.fill();
    // mist between layers
    if (li < canopy.length - 1) {
      const mist = ctx.createLinearGradient(0, baseY, 0, baseY + h * 0.12);
      mist.addColorStop(0, 'rgba(255,255,255,0.25)');
      mist.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = mist;
      ctx.fillRect(0, baseY, w, h * 0.12);
    }
  });
}

// ---- Shared figure/prop primitives (used to compose the realistic scenes below) ----

function drawStars(ctx, w, h, rng, { count = 90, maxY = 0.62 } = {}) {
  for (let i = 0; i < count; i++) {
    const x = rng() * w;
    const y = rng() * h * maxY;
    const r = rng() * 1.6 + 0.3;
    ctx.globalAlpha = 0.35 + rng() * 0.65;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
}

function drawMoon(ctx, w, h, { x = 0.78, y = 0.22, r = 0.055, color = '#f4f2e8', craterColor = 'rgba(0,0,0,0.08)' } = {}) {
  const cx = w * x, cy = h * y, rad = w * r;
  const glow = ctx.createRadialGradient(cx, cy, rad * 0.5, cx, cy, rad * 4);
  glow.addColorStop(0, 'rgba(255,255,240,0.35)');
  glow.addColorStop(1, 'rgba(255,255,240,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(cx, cy, rad * 4, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, rad, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = craterColor;
  [[-0.3, -0.2, 0.28], [0.25, 0.15, 0.22], [-0.05, 0.35, 0.18]].forEach(([dx, dy, dr]) => {
    ctx.beginPath();
    ctx.arc(cx + dx * rad, cy + dy * rad, dr * rad, 0, Math.PI * 2);
    ctx.fill();
  });
}

// A layered pine — a few stacked triangles, wider at the base.
function drawPineTree(ctx, x, baseY, scale, color) {
  const w = 34 * scale, h = 92 * scale;
  const tiers = 3;
  ctx.fillStyle = color;
  for (let t = 0; t < tiers; t++) {
    const tw = w * (1 - t * 0.22);
    const th = (h / tiers) * 1.25;
    const ty = baseY - h + t * (h / tiers) * 0.85;
    ctx.beginPath();
    ctx.moveTo(x, ty);
    ctx.lineTo(x - tw / 2, ty + th);
    ctx.lineTo(x + tw / 2, ty + th);
    ctx.closePath();
    ctx.fill();
  }
  ctx.fillRect(x - 3 * scale, baseY - 8 * scale, 6 * scale, 10 * scale);
}

// A round-canopy deciduous tree — trunk + overlapping canopy blobs.
function drawRoundTree(ctx, x, baseY, scale, { trunk, canopy }) {
  const trunkH = 26 * scale, trunkW = 7 * scale;
  ctx.fillStyle = trunk;
  ctx.fillRect(x - trunkW / 2, baseY - trunkH, trunkW, trunkH);
  ctx.fillStyle = canopy;
  const r = 26 * scale;
  const puffs = [[-0.5, -0.2], [0.5, -0.2], [0, -0.55], [-0.22, -0.75], [0.22, -0.75]];
  puffs.forEach(([dx, dy]) => {
    ctx.beginPath();
    ctx.arc(x + dx * r, baseY - trunkH + dy * r, r * (0.62 + Math.abs(dy) * 0.1), 0, Math.PI * 2);
    ctx.fill();
  });
}

// A solid (non-stick) standing figure: a filled, tapered torso mass and
// thick rounded-cap limbs, so it reads as a body silhouette rather than a
// wireframe. `armL`/`armR` aim each arm — 'down', 'out' (reaching sideways,
// e.g. to hold another figure's hand), 'up', or 'fwd'. Returns the shoulder/
// hip/head geometry so callers can position a joined hand, a carried child,
// etc. relative to this figure.
function drawPersonSolid(ctx, x, baseY, scale, color, { armL = 'down', armR = 'down', legSpread = 1, headOffset = 0 } = {}) {
  const s = scale;
  const shoulderY = baseY - 50 * s;
  const hipY = baseY - 22 * s;
  const headR = 10.5 * s;
  const tw = 19 * s;

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  // legs — tapered filled shapes (not thin strokes), rounded at the foot
  ctx.lineWidth = 13 * s;
  ctx.beginPath();
  ctx.moveTo(x - 4 * s, hipY);
  ctx.lineTo(x - 7 * s * legSpread, baseY);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + 4 * s, hipY);
  ctx.lineTo(x + 7 * s * legSpread, baseY);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x - tw / 2, shoulderY);
  ctx.quadraticCurveTo(x - tw / 2 - 3 * s, (shoulderY + hipY) / 2, x - tw / 2 + 4 * s, hipY);
  ctx.lineTo(x + tw / 2 - 4 * s, hipY);
  ctx.quadraticCurveTo(x + tw / 2 + 3 * s, (shoulderY + hipY) / 2, x + tw / 2, shoulderY);
  ctx.closePath();
  ctx.fill();

  const armEnd = (side, dir) => {
    if (dir === 'out') return [x + side * 24 * s, shoulderY + 8 * s];
    if (dir === 'up') return [x + side * 13 * s, shoulderY - 18 * s];
    if (dir === 'fwd') return [x + side * 11 * s, shoulderY + 18 * s];
    return [x + side * 6 * s, hipY + 6 * s]; // down
  };
  ctx.lineWidth = 10 * s;
  [[-1, armL], [1, armR]].forEach(([side, dir]) => {
    const [ex, ey] = armEnd(side, dir);
    ctx.beginPath();
    ctx.moveTo(x + side * tw * 0.32, shoulderY + 4 * s);
    ctx.lineTo(ex, ey);
    ctx.stroke();
  });

  ctx.beginPath();
  ctx.arc(x + headOffset * s, shoulderY - headR - 1 * s, headR, 0, Math.PI * 2);
  ctx.fill();

  return { shoulderY, hipY, headR, handOut: side => armEnd(side, 'out') };
}

// A solid seated figure, sitting on a bench/ground top at seatY. `lean`
// tilts the whole figure a touch toward a companion (positive = toward +x).
function drawPersonSeated(ctx, x, seatY, scale, color, { lean = 0 } = {}) {
  const s = scale;
  const hipY = seatY - 5 * s;
  const shoulderY = seatY - 38 * s;
  const headR = 9.5 * s;
  const tw = 18 * s;

  ctx.strokeStyle = color;
  ctx.fillStyle = color;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  ctx.lineWidth = 12 * s;
  ctx.beginPath();
  ctx.moveTo(x, hipY);
  ctx.lineTo(x + 17 * s, hipY + 2 * s);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(x + 17 * s, hipY + 2 * s);
  ctx.lineTo(x + 16 * s, hipY + 20 * s);
  ctx.stroke();

  ctx.beginPath();
  ctx.moveTo(x - tw / 2 + lean * s, shoulderY);
  ctx.quadraticCurveTo(x - tw / 2 - 3 * s + lean * s, (shoulderY + hipY) / 2, x - tw / 2 + 3 * s, hipY);
  ctx.lineTo(x + tw / 2 - 3 * s, hipY);
  ctx.quadraticCurveTo(x + tw / 2 + 3 * s + lean * s, (shoulderY + hipY) / 2, x + tw / 2 + lean * s, shoulderY);
  ctx.closePath();
  ctx.fill();

  ctx.lineWidth = 9 * s;
  ctx.beginPath();
  ctx.moveTo(x + lean * s, shoulderY + 5 * s);
  ctx.lineTo(x + 5 * s, hipY + 3 * s);
  ctx.stroke();

  ctx.beginPath();
  ctx.arc(x + lean * s, shoulderY - headR - 1 * s, headR, 0, Math.PI * 2);
  ctx.fill();
}

// Legacy call-site-compatible aliases (city street + park scenes already use
// these names) — now solid-bodied instead of the old thin stick-figure lines.
function drawPersonWalk(ctx, x, baseY, scale, color, stride = 1) {
  drawPersonSolid(ctx, x, baseY, scale, color, {
    armL: stride >= 0 ? 'fwd' : 'down',
    armR: stride >= 0 ? 'down' : 'fwd',
    legSpread: 1.3,
  });
}

function drawPersonSit(ctx, x, seatY, scale, color) {
  drawPersonSeated(ctx, x, seatY, scale, color, {});
}

// A small filled circle where two figures' hands meet — reads as clasped
// hands at silhouette scale.
function drawClaspedHands(ctx, x, y, scale, color) {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(x, y, 3.4 * scale, 0, Math.PI * 2);
  ctx.fill();
}

function drawBench(ctx, x, baseY, width, color) {
  const legH = 16;
  ctx.strokeStyle = color;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(x - width / 2, baseY - legH);
  ctx.lineTo(x - width / 2, baseY);
  ctx.moveTo(x + width / 2, baseY - legH);
  ctx.lineTo(x + width / 2, baseY);
  ctx.stroke();
  ctx.fillStyle = color;
  ctx.fillRect(x - width / 2 - 4, baseY - legH - 6, width + 8, 6);
  ctx.fillRect(x - width / 2 - 4, baseY - legH - 34, width + 8, 6);
}

// A house silhouette with a gable or hip roof and a couple of glowing windows.
function drawHouse(ctx, x, baseY, w, h, { roof, wall, roofType = 'gable', lit = true }, rng) {
  const roofH = h * 0.42;
  ctx.fillStyle = wall;
  ctx.fillRect(x, baseY - h, w, h - roofH);
  ctx.fillStyle = roof;
  ctx.beginPath();
  if (roofType === 'hip') {
    ctx.moveTo(x - w * 0.06, baseY - h + roofH);
    ctx.lineTo(x + w * 0.3, baseY - h);
    ctx.lineTo(x + w * 0.7, baseY - h);
    ctx.lineTo(x + w * 1.06, baseY - h + roofH);
    ctx.closePath();
  } else {
    ctx.moveTo(x - w * 0.08, baseY - h + roofH);
    ctx.lineTo(x + w * 0.5, baseY - h - roofH * 0.35);
    ctx.lineTo(x + w * 1.08, baseY - h + roofH);
    ctx.closePath();
  }
  ctx.fill();
  if (lit) {
    ctx.fillStyle = 'rgba(255,214,140,0.85)';
    const winW = w * 0.16, winH = (h - roofH) * 0.3;
    ctx.fillRect(x + w * 0.18, baseY - h + roofH + (h - roofH) * 0.32, winW, winH);
    if (rng ? rng() > 0.35 : true) {
      ctx.fillRect(x + w * 0.62, baseY - h + roofH + (h - roofH) * 0.32, winW, winH);
    }
  }
}

function drawStreetlight(ctx, x, baseY, height, { pole = '#1a1a1a', glow = 'rgba(255,220,140,0.55)' } = {}) {
  ctx.strokeStyle = pole;
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(x, baseY);
  ctx.lineTo(x, baseY - height);
  ctx.stroke();
  ctx.fillStyle = pole;
  ctx.beginPath();
  ctx.arc(x, baseY - height, 6, 0, Math.PI * 2);
  ctx.fill();
  const g = ctx.createRadialGradient(x, baseY - height, 2, x, baseY - height, 46);
  g.addColorStop(0, glow);
  g.addColorStop(1, 'rgba(255,220,140,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(x, baseY - height, 46, 0, Math.PI * 2);
  ctx.fill();
}

// A tall skyline building with a grid of randomly-lit windows.
function drawBuilding(ctx, x, baseY, w, h, { wall, lit = 'rgba(255,220,150,0.8)', unlit = 'rgba(0,0,0,0.15)' }, rng) {
  ctx.fillStyle = wall;
  ctx.fillRect(x, baseY - h, w, h);
  const cols = Math.max(2, Math.floor(w / 14));
  const rows = Math.max(3, Math.floor(h / 18));
  const gapX = w / cols, gapY = h / rows;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      ctx.fillStyle = rng() > 0.4 ? lit : unlit;
      ctx.fillRect(x + c * gapX + gapX * 0.25, baseY - h + r * gapY + gapY * 0.25, gapX * 0.5, gapY * 0.5);
    }
  }
}

// One waterfall ribbon dropping from topY to bottomY, with a mist puff at its base.
function drawWaterfallRibbon(ctx, x, topY, bottomY, width, { top = '#eaf7ff', bottom = '#bfe3f2', mist = 'rgba(255,255,255,0.8)' }, rng) {
  const g = ctx.createLinearGradient(0, topY, 0, bottomY);
  g.addColorStop(0, top);
  g.addColorStop(1, bottom);
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(x - width / 2, topY);
  ctx.lineTo(x + width / 2, topY);
  ctx.lineTo(x + width / 2 + width * 0.15, bottomY);
  ctx.lineTo(x - width / 2 - width * 0.15, bottomY);
  ctx.closePath();
  ctx.fill();
  // vertical streak lines for a sense of falling motion
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = Math.max(1, width * 0.03);
  const streaks = Math.max(3, Math.round(width / 10));
  for (let i = 0; i < streaks; i++) {
    const sx = x - width / 2 + (width / streaks) * (i + 0.5);
    ctx.globalAlpha = 0.3 + rng() * 0.4;
    ctx.beginPath();
    ctx.moveTo(sx, topY + (bottomY - topY) * 0.1);
    ctx.lineTo(sx + (rng() - 0.5) * 6, bottomY - (bottomY - topY) * 0.08);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
  // mist at the base
  ctx.fillStyle = mist;
  ctx.globalAlpha = 0.5;
  ctx.beginPath();
  ctx.ellipse(x, bottomY, width * 0.9, width * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
}

// ---- Realistic composed scenes ----

function drawForestScene(ctx, w, h, { sky, ground, pine, round, night, moon }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  if (night) {
    drawStars(ctx, w, h, rng, { count: 110, maxY: 0.55 });
    if (moon) drawMoon(ctx, w, h, moon);
  }
  const horizon = h * 0.66;
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);
  // back row, small & muted; front row, larger & darker — a cheap sense of depth
  const rows = [
    { y: horizon + h * 0.02, scale: 0.55, alpha: 0.75, count: 9 },
    { y: horizon + h * 0.09, scale: 0.8, alpha: 0.9, count: 7 },
    { y: horizon + h * 0.19, scale: 1.15, alpha: 1, count: 5 },
  ];
  rows.forEach(row => {
    ctx.globalAlpha = row.alpha;
    for (let i = 0; i < row.count; i++) {
      const x = (w / row.count) * (i + 0.5) + (rng() - 0.5) * (w / row.count) * 0.7;
      if (rng() > 0.45) drawPineTree(ctx, x, row.y, row.scale, pine);
      else drawRoundTree(ctx, x, row.y, row.scale, { trunk: '#3b2a1c', canopy: round });
    }
  });
  ctx.globalAlpha = 1;
}

// One receding ridge layer: soft, rounded rolling hills (bezier humps, no
// straight edges) that dip into a gentle, wide saddle in the middle for the
// falls to drop through — reads as a natural valley, not a carved fortress.
function drawRidgeLayer(ctx, w, h, { baseY, peakY, notchY, notchWidth, color }, rng) {
  const notchL = w * (0.5 - notchWidth / 2), notchR = w * (0.5 + notchWidth / 2);
  const jag = h * 0.015;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.moveTo(0, h);
  ctx.lineTo(0, baseY + (rng() - 0.5) * jag);
  // Left shoulder: one broad rounded hill rising from the edge to the saddle.
  ctx.quadraticCurveTo(w * 0.11, peakY - h * 0.02 * rng(), w * 0.22, peakY);
  ctx.quadraticCurveTo(w * 0.32, peakY + (notchY - peakY) * 0.35, notchL, notchY - h * 0.01);
  // Wide, softly rounded saddle — no sharp point at the bottom.
  ctx.quadraticCurveTo(w * 0.5, notchY + h * 0.015, notchR, notchY - h * 0.01);
  // Right shoulder, mirrored with independent jitter for a natural look.
  ctx.quadraticCurveTo(w * 0.68, peakY + (notchY - peakY) * 0.35, w * 0.78, peakY - h * 0.015 * rng());
  ctx.quadraticCurveTo(w * 0.89, peakY - h * 0.02 * rng(), w, baseY + (rng() - 0.5) * jag);
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fill();
}

function drawWaterfallScene(ctx, w, h, { sky, ridges, falls, pool, pine, widths = [0.07, 0.07], mist }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });

  // Three ridge layers receding toward the valley notch the falls drop
  // through — furthest is palest/bluest, nearest is darkest, for depth.
  const notchYs = [h * 0.42, h * 0.5, h * 0.58];
  ridges.forEach((color, li) => {
    drawRidgeLayer(ctx, w, h, {
      baseY: h * (0.58 + li * 0.1),
      peakY: h * (0.3 + li * 0.08),
      notchY: notchYs[li],
      notchWidth: 0.22 + li * 0.05,
      color,
    }, rng);
  });

  // Falls drop from the nearest ridge's notch down to the pool.
  const topY = notchYs[notchYs.length - 1] - h * 0.01;
  const bottomY = h * 0.8;
  const gap = (w * (0.22 + (ridges.length - 1) * 0.05)) / (widths.length + 1);
  const notchCenter = w * 0.5 - (w * (0.22 + (ridges.length - 1) * 0.05)) / 2;
  widths.forEach((wRatio, i) => {
    const cx = notchCenter + gap * (i + 1);
    drawWaterfallRibbon(ctx, cx, topY, bottomY, w * wRatio, { ...falls, mist }, rng);
  });

  // Pool at the base, with a lighter near-shore band.
  ctx.fillStyle = pool.deep;
  ctx.fillRect(0, bottomY, w, h - bottomY);
  ctx.fillStyle = pool.shallow;
  ctx.globalAlpha = 0.6;
  ctx.fillRect(0, bottomY, w, (h - bottomY) * 0.4);
  ctx.globalAlpha = 1;

  // Dark evergreen clusters framing the foreground on both sides.
  const leftTrees = [[w * 0.02, 1.3], [w * 0.09, 1.0], [w * 0.15, 0.75], [w * 0.04, 0.6]];
  const rightTrees = [[w * 0.98, 1.3], [w * 0.91, 1.0], [w * 0.85, 0.75], [w * 0.96, 0.6]];
  [...leftTrees, ...rightTrees].forEach(([x, scale]) => drawPineTree(ctx, x, h * 0.98, scale, pine));
}

function drawNeighborhoodScene(ctx, w, h, { sky, houses, roofColors, street, sidewalk, night, moon }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  if (night) {
    drawStars(ctx, w, h, rng, { count: 70, maxY: 0.45 });
    if (moon) drawMoon(ctx, w, h, moon);
  }
  const horizon = h * 0.62;
  // houses along the horizon
  const count = 5;
  const hw = w / count;
  for (let i = 0; i < count; i++) {
    const houseW = hw * (0.62 + rng() * 0.12);
    const houseH = h * (0.16 + rng() * 0.06);
    const x = hw * i + (hw - houseW) / 2;
    drawHouse(ctx, x, horizon, houseW, houseH, {
      roof: roofColors[i % roofColors.length],
      wall: houses[i % houses.length],
      roofType: rng() > 0.5 ? 'gable' : 'hip',
      lit: night || rng() > 0.4,
    }, rng);
  }
  // street + sidewalk
  ctx.fillStyle = sidewalk;
  ctx.fillRect(0, horizon, w, h * 0.06);
  ctx.fillStyle = street;
  ctx.fillRect(0, horizon + h * 0.06, w, h - horizon - h * 0.06);
  ctx.strokeStyle = 'rgba(255,255,255,0.35)';
  ctx.setLineDash([22, 18]);
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.9);
  ctx.lineTo(w, h * 0.9);
  ctx.stroke();
  ctx.setLineDash([]);
  drawStreetlight(ctx, w * 0.22, horizon + h * 0.06, h * 0.16);
  drawStreetlight(ctx, w * 0.78, horizon + h * 0.06, h * 0.16);
}

function drawCityStreetScene(ctx, w, h, { sky, buildings, street, night, moon }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  if (night) {
    drawStars(ctx, w, h, rng, { count: 60, maxY: 0.4 });
    if (moon) drawMoon(ctx, w, h, moon);
  }
  const horizon = h * 0.68;
  let x = 0;
  while (x < w) {
    const bw = w * (0.09 + rng() * 0.07);
    const bh = h * (0.14 + rng() * 0.24);
    drawBuilding(ctx, x, horizon, bw, bh, { wall: buildings[Math.floor(rng() * buildings.length)] }, rng);
    x += bw + w * 0.01;
  }
  ctx.fillStyle = street;
  ctx.fillRect(0, horizon, w, h - horizon);
  ctx.strokeStyle = 'rgba(255,255,255,0.3)';
  ctx.setLineDash([20, 16]);
  ctx.lineWidth = 4;
  ctx.beginPath();
  ctx.moveTo(0, h * 0.9);
  ctx.lineTo(w, h * 0.9);
  ctx.stroke();
  ctx.setLineDash([]);
  drawStreetlight(ctx, w * 0.15, horizon, h * 0.15);
  drawStreetlight(ctx, w * 0.85, horizon, h * 0.15);
  // a couple of tiny pedestrians on the sidewalk
  drawPersonWalk(ctx, w * 0.42, horizon + h * 0.03, 0.55, 'rgba(20,20,25,0.85)', 1);
  drawPersonWalk(ctx, w * 0.58, horizon + h * 0.035, 0.5, 'rgba(20,20,25,0.85)', -1);
}

function drawParkWalkScene(ctx, w, h, { sky, grass, path, pine, round }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.6;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  ctx.fillStyle = path;
  ctx.beginPath();
  ctx.moveTo(w * 0.42, horizon);
  ctx.bezierCurveTo(w * 0.3, h * 0.75, w * 0.6, h * 0.85, w * 0.5, h);
  ctx.lineTo(w * 0.68, h);
  ctx.bezierCurveTo(w * 0.72, h * 0.82, w * 0.5, h * 0.72, w * 0.58, horizon);
  ctx.closePath();
  ctx.fill();
  [[w * 0.14, horizon + h * 0.03, 0.85], [w * 0.85, horizon + h * 0.05, 0.7], [w * 0.05, horizon + h * 0.13, 1.0]].forEach(([x, y, s]) => {
    if (rng() > 0.5) drawRoundTree(ctx, x, y, s, { trunk: '#3b2a1c', canopy: round });
    else drawPineTree(ctx, x, y, s, pine);
  });
  drawPersonWalk(ctx, w * 0.47, h * 0.86, 0.85, 'rgba(30,25,25,0.88)', 1);
  drawPersonWalk(ctx, w * 0.56, h * 0.79, 0.65, 'rgba(30,25,25,0.8)', -1);
}

function drawParkSitScene(ctx, w, h, { sky, grass, canopy }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.64;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  // one large shade tree, canopy arcing over the top of the frame
  const tx = w * 0.28;
  ctx.fillStyle = '#3b2a1c';
  ctx.fillRect(tx - 10, horizon - h * 0.3, 20, h * 0.3);
  ctx.fillStyle = canopy;
  ctx.beginPath();
  ctx.ellipse(tx, horizon - h * 0.32, w * 0.34, h * 0.22, 0, 0, Math.PI * 2);
  ctx.fill();
  const benchY = horizon + h * 0.14;
  drawBench(ctx, w * 0.56, benchY, w * 0.22, 'rgba(60,40,25,0.9)');
  drawPersonSit(ctx, w * 0.52, benchY, 0.9, 'rgba(30,25,25,0.88)');
  drawPersonSit(ctx, w * 0.63, benchY, 0.85, 'rgba(30,25,25,0.8)');
}

// ---- Relationships (people together — solid silhouettes, no stick figures) ----

function drawCoupleWalkScene(ctx, w, h, { sky, sea, sand, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.55;
  ctx.fillStyle = sea;
  ctx.fillRect(0, horizon, w, h * 0.22);
  ctx.fillStyle = sand;
  ctx.beginPath();
  ctx.moveTo(0, h);
  ctx.lineTo(0, horizon + h * 0.2);
  ctx.quadraticCurveTo(w * 0.5, horizon + h * 0.12, w, horizon + h * 0.2);
  ctx.lineTo(w, h);
  ctx.closePath();
  ctx.fill();
  const baseY = horizon + h * 0.3;
  const p1 = drawPersonSolid(ctx, w * 0.44, baseY, 1.05, figure, { armL: 'down', armR: 'out' });
  drawPersonSolid(ctx, w * 0.56, baseY, 1.0, figure, { armL: 'out', armR: 'down' });
  drawClaspedHands(ctx, (w * 0.44 + w * 0.56) / 2, p1.shoulderY + 8, 1, figure);
}

function drawParentChildWalkScene(ctx, w, h, { sky, grass, path, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.62;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  ctx.fillStyle = path;
  ctx.beginPath();
  ctx.moveTo(w * 0.3, horizon);
  ctx.quadraticCurveTo(w * 0.5, h * 0.85, w * 0.42, h);
  ctx.lineTo(w * 0.66, h);
  ctx.quadraticCurveTo(w * 0.62, h * 0.85, w * 0.5, horizon);
  ctx.closePath();
  ctx.fill();
  const baseY = h * 0.92;
  const adult = drawPersonSolid(ctx, w * 0.46, baseY, 1.1, figure, { armL: 'down', armR: 'out' });
  drawPersonSolid(ctx, w * 0.55, baseY + 6, 0.62, figure, { armL: 'out', armR: 'down' });
  drawClaspedHands(ctx, w * 0.505, adult.shoulderY + 22, 0.8, figure);
}

function drawFamilyFourScene(ctx, w, h, { sky, grass, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.66;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  const baseY = h * 0.94;
  const xs = [0.28, 0.42, 0.58, 0.72];
  const scales = [1.05, 0.6, 0.58, 1.0];
  const arms = [
    { armL: 'down', armR: 'out' },
    { armL: 'out', armR: 'out' },
    { armL: 'out', armR: 'out' },
    { armL: 'out', armR: 'down' },
  ];
  const shoulders = xs.map((xr, i) => drawPersonSolid(ctx, w * xr, baseY, scales[i], figure, arms[i]).shoulderY);
  for (let i = 0; i < xs.length - 1; i++) {
    drawClaspedHands(ctx, w * (xs[i] + xs[i + 1]) / 2, Math.min(shoulders[i], shoulders[i + 1]) + 16, 0.75, figure);
  }
}

function drawEmbraceScene(ctx, w, h, { sky, ground, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.7;
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);
  const baseY = h * 0.92;
  drawPersonSolid(ctx, w * 0.46, baseY, 1.15, figure, { armL: 'down', armR: 'up' });
  drawPersonSolid(ctx, w * 0.54, baseY - 2, 1.1, figure, { armL: 'up', armR: 'down' });
}

function drawFriendsDockScene(ctx, w, h, { sky, water, dock, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.5;
  ctx.fillStyle = water;
  ctx.fillRect(0, horizon, w, h - horizon);
  ctx.globalAlpha = 0.18;
  ctx.fillStyle = '#ffffff';
  for (let i = 0; i < 16; i++) {
    const y = horizon + rng() * (h - horizon);
    ctx.fillRect(w * 0.1 + rng() * w * 0.8, y, w * (0.05 + rng() * 0.1), 2);
  }
  ctx.globalAlpha = 1;
  const dockY = h * 0.62;
  ctx.fillStyle = dock;
  ctx.fillRect(w * 0.2, dockY, w * 0.6, h * 0.05);
  for (let i = 0; i < 4; i++) {
    ctx.fillRect(w * 0.22 + i * (w * 0.19), dockY, w * 0.02, h - dockY);
  }
  const seatY = dockY;
  drawPersonSeated(ctx, w * 0.38, seatY, 0.9, figure, { lean: 4 });
  drawPersonSeated(ctx, w * 0.5, seatY, 0.85, figure, { lean: 0 });
  drawPersonSeated(ctx, w * 0.62, seatY, 0.9, figure, { lean: -4 });
}

function drawShoulderCarryScene(ctx, w, h, { sky, grass, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.66;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  const baseY = h * 0.94;
  const adult = drawPersonSolid(ctx, w * 0.5, baseY, 1.2, figure, { armL: 'up', armR: 'up' });
  // child seated atop the adult's shoulders — small torso + head, legs hanging past the neck
  const s = 0.6;
  const childHipY = adult.shoulderY - 6;
  ctx.strokeStyle = figure;
  ctx.fillStyle = figure;
  ctx.lineCap = 'round';
  ctx.lineWidth = 7 * s;
  ctx.beginPath();
  ctx.moveTo(w * 0.5 - 5 * s, childHipY);
  ctx.lineTo(w * 0.5 - 8 * s, childHipY + 26 * s);
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(w * 0.5 + 5 * s, childHipY);
  ctx.lineTo(w * 0.5 + 8 * s, childHipY + 26 * s);
  ctx.stroke();
  const tw = 13 * s;
  const childShoulderY = childHipY - 26 * s;
  ctx.beginPath();
  ctx.moveTo(w * 0.5 - tw / 2, childShoulderY);
  ctx.quadraticCurveTo(w * 0.5 - tw / 2 - 2 * s, (childShoulderY + childHipY) / 2, w * 0.5 - tw / 2 + 2 * s, childHipY);
  ctx.lineTo(w * 0.5 + tw / 2 - 2 * s, childHipY);
  ctx.quadraticCurveTo(w * 0.5 + tw / 2 + 2 * s, (childShoulderY + childHipY) / 2, w * 0.5 + tw / 2, childShoulderY);
  ctx.closePath();
  ctx.fill();
  ctx.beginPath();
  ctx.arc(w * 0.5, childShoulderY - 8 * s, 7.5 * s, 0, Math.PI * 2);
  ctx.fill();
  // child's arms reaching down to the adult's raised hands
  ctx.lineWidth = 5 * s;
  ctx.beginPath();
  ctx.moveTo(w * 0.5 - tw * 0.35, childShoulderY + 3 * s);
  ctx.lineTo(w * 0.5 - 12 * s, childHipY + 10 * s);
  ctx.moveTo(w * 0.5 + tw * 0.35, childShoulderY + 3 * s);
  ctx.lineTo(w * 0.5 + 12 * s, childHipY + 10 * s);
  ctx.stroke();
}

function drawCoupleBenchScene(ctx, w, h, { sky, ground, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.68;
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);
  const benchY = horizon + h * 0.1;
  drawBench(ctx, w * 0.5, benchY, w * 0.26, 'rgba(40,30,20,0.85)');
  drawPersonSeated(ctx, w * 0.44, benchY, 0.95, figure, { lean: 3 });
  drawPersonSeated(ctx, w * 0.56, benchY, 0.95, figure, { lean: -3 });
}

function drawGrandparentScene(ctx, w, h, { sky, grass, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.66;
  ctx.fillStyle = grass;
  ctx.fillRect(0, horizon, w, h - horizon);
  const benchY = horizon + h * 0.12;
  drawBench(ctx, w * 0.5, benchY, w * 0.24, 'rgba(60,40,25,0.9)');
  drawPersonSeated(ctx, w * 0.42, benchY, 1.05, figure, { lean: 3 });
  drawPersonSeated(ctx, w * 0.57, benchY, 0.6, figure, { lean: -2 });
}

function drawCampfireScene(ctx, w, h, { sky, ground, figure, fire, night, moon }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  if (night) {
    drawStars(ctx, w, h, rng, { count: 100, maxY: 0.55 });
    if (moon) drawMoon(ctx, w, h, moon);
  }
  const horizon = h * 0.68;
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);
  const fireX = w * 0.5, fireY = h * 0.86;
  const glow = ctx.createRadialGradient(fireX, fireY, 4, fireX, fireY, w * 0.28);
  glow.addColorStop(0, 'rgba(255,180,90,0.55)');
  glow.addColorStop(1, 'rgba(255,180,90,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(fireX, fireY, w * 0.28, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = fire;
  ctx.beginPath();
  ctx.moveTo(fireX, fireY - 26);
  ctx.quadraticCurveTo(fireX + 12, fireY - 8, fireX + 6, fireY + 4);
  ctx.quadraticCurveTo(fireX + 3, fireY - 6, fireX, fireY - 26);
  ctx.moveTo(fireX, fireY - 20);
  ctx.quadraticCurveTo(fireX - 10, fireY - 4, fireX - 2, fireY + 4);
  ctx.quadraticCurveTo(fireX - 1, fireY - 8, fireX, fireY - 20);
  ctx.fill();
  const seatY = fireY + 8;
  drawPersonSeated(ctx, fireX - w * 0.16, seatY, 0.85, figure, { lean: 3 });
  drawPersonSeated(ctx, fireX + w * 0.16, seatY, 0.85, figure, { lean: -3 });
  drawPersonSeated(ctx, fireX - w * 0.05, seatY - h * 0.02, 0.75, figure, { lean: 2 });
}

function drawWeddingArchScene(ctx, w, h, { sky, ground, arch, figure }, rng) {
  linear(ctx, w, h, sky, { angle: 90 });
  const horizon = h * 0.72;
  ctx.fillStyle = ground;
  ctx.fillRect(0, horizon, w, h - horizon);
  // simple arch: two posts + a curved top, with small flower dots
  const archTop = h * 0.18, archBaseY = h * 0.84, archW = w * 0.34;
  ctx.strokeStyle = arch;
  ctx.lineWidth = w * 0.02;
  ctx.lineCap = 'round';
  ctx.beginPath();
  ctx.moveTo(w * 0.5 - archW / 2, archBaseY);
  ctx.lineTo(w * 0.5 - archW / 2, archTop + h * 0.08);
  ctx.quadraticCurveTo(w * 0.5 - archW / 2, archTop, w * 0.5, archTop);
  ctx.quadraticCurveTo(w * 0.5 + archW / 2, archTop, w * 0.5 + archW / 2, archTop + h * 0.08);
  ctx.lineTo(w * 0.5 + archW / 2, archBaseY);
  ctx.stroke();
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  for (let i = 0; i < 14; i++) {
    const t = i / 13;
    const ax = w * 0.5 - archW / 2 + t * archW;
    const ay = archTop + h * 0.02 + Math.sin(t * Math.PI) * -h * 0.06;
    ctx.beginPath();
    ctx.arc(ax, ay, w * 0.008, 0, Math.PI * 2);
    ctx.fill();
  }
  const baseY = h * 0.88;
  const p1 = drawPersonSolid(ctx, w * 0.46, baseY, 1.0, figure, { armL: 'down', armR: 'out' });
  drawPersonSolid(ctx, w * 0.54, baseY, 1.0, figure, { armL: 'out', armR: 'down' });
  drawClaspedHands(ctx, w * 0.5, p1.shoulderY + 8, 0.9, figure);
}

// ---- Textures & patterns ----

function drawGrain(ctx, w, h, base, { dots = 2600, light = true }, rng) {
  linear(ctx, w, h, base, { angle: 115 });
  for (let i = 0; i < dots; i++) {
    ctx.globalAlpha = 0.03 + rng() * 0.07;
    ctx.fillStyle = (light ? rng() > 0.35 : rng() > 0.65) ? '#ffffff' : '#000000';
    const s = 1 + rng() * 3;
    ctx.fillRect(rng() * w, rng() * h, s, s);
  }
  ctx.globalAlpha = 1;
}

function drawDots(ctx, w, h, { base, dot, gap = 64, r = 5 }) {
  linear(ctx, w, h, base, { angle: 135 });
  ctx.fillStyle = dot;
  for (let y = gap / 2; y < h; y += gap) {
    for (let x = gap / 2; x < w; x += gap) {
      ctx.beginPath();
      ctx.arc(x + ((y / gap) % 2 ? gap / 2 : 0), y, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawDiagonals(ctx, w, h, { base, line, gap = 70, width = 10 }) {
  linear(ctx, w, h, base, { angle: 45 });
  ctx.strokeStyle = line;
  ctx.lineWidth = width;
  for (let x = -h; x < w + h; x += gap) {
    ctx.beginPath();
    ctx.moveTo(x, 0);
    ctx.lineTo(x + h, h);
    ctx.stroke();
  }
}

function drawWaves(ctx, w, h, { base, line, rows = 14 }) {
  linear(ctx, w, h, base, { angle: 90 });
  ctx.strokeStyle = line;
  ctx.lineWidth = 4;
  const gap = h / rows;
  for (let r = 0; r <= rows; r++) {
    const y = r * gap;
    ctx.beginPath();
    for (let x = 0; x <= w; x += 8) {
      const yy = y + Math.sin((x / w) * Math.PI * 6 + r) * gap * 0.18;
      x === 0 ? ctx.moveTo(x, yy) : ctx.lineTo(x, yy);
    }
    ctx.stroke();
  }
}

function drawRings(ctx, w, h, { base, ring, count = 9 }) {
  linear(ctx, w, h, base, { angle: 90 });
  ctx.strokeStyle = ring;
  ctx.lineWidth = 3;
  const cx = w / 2, cy = h * 0.42;
  for (let i = 1; i <= count; i++) {
    ctx.beginPath();
    ctx.arc(cx, cy, (Math.max(w, h) / count) * i * 0.55, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawRays(ctx, w, h, { base, ray, count = 24 }) {
  linear(ctx, w, h, base, { angle: 90 });
  ctx.fillStyle = ray;
  const cx = w / 2, cy = h * 0.38, R = Math.max(w, h) * 1.4;
  for (let i = 0; i < count; i++) {
    if (i % 2) continue;
    const a0 = (i / count) * Math.PI * 2;
    const a1 = ((i + 1) / count) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, R, a0, a1);
    ctx.closePath();
    ctx.fill();
  }
}

// ---- Library definition ----

const defs = [];
const add = (id, name, category, draw) => defs.push({ id, name, category, draw });

// Nature (24)
const MOUNTAIN_SETS = [
  ['Dusk Peaks', { sky: ['#2b1055', '#7597de'], sun: { color: '#ffd9a0', x: 0.7, y: 0.3, r: 0.07 }, layers: ['#4a3f78', '#372c5e', '#241b45', '#150f2d'] }],
  ['Golden Range', { sky: ['#ff9966', '#ff5e62'], sun: { color: '#fff3c4', x: 0.5, y: 0.28, r: 0.09 }, layers: ['#b64f45', '#8c3a38', '#5e2529', '#3a161c'] }],
  ['Misty Morning', { sky: ['#e0eafc', '#cfdef3'], sun: null, layers: ['#a8b8d0', '#8398b8', '#5f7898', '#3d5876'] }],
  ['Teal Summits', { sky: ['#0f2027', '#2c5364'], sun: { color: '#a5ffd6', x: 0.32, y: 0.26, r: 0.06 }, layers: ['#24555e', '#1b414c', '#122d38', '#0a1c26'] }],
  ['Rose Alps', { sky: ['#fbc2eb', '#a6c1ee'], sun: null, layers: ['#c091c4', '#96699f', '#6d477a', '#452b53'] }],
  ['Night Ridge', { sky: ['#0b1026', '#2c3e6b'], sun: { color: '#f4f6ff', x: 0.76, y: 0.2, r: 0.045 }, layers: ['#27335c', '#1c2547', '#121933', '#090d1f'] }],
  ['Snow Crest', { sky: ['#a7c7e7', '#e8f1fa'], sun: null, layers: ['#5c7a94', '#496279', '#374b5e', '#263444'], snow: true }],
  ['Alpine Gold', { sky: ['#ffb75e', '#ed8f03'], sun: { color: '#fff3c4', x: 0.28, y: 0.24, r: 0.06 }, layers: ['#5c4a3d', '#453830', '#2e2622', '#1a1614'], snow: true }],
  ['Starlit Range', { sky: ['#050814', '#111a33'], sun: null, moon: { x: 0.72, y: 0.16, r: 0.05 }, stars: true, layers: ['#39456b', '#2a3352', '#1c2338', '#10141f'], snow: true }],
];
MOUNTAIN_SETS.forEach(([name, cfg], i) =>
  add(`mountains-${i}`, name, 'nature', (ctx, w, h) => drawMountains(ctx, w, h, cfg, mulberry32(hashSeed(`mountains-${i}`)))));

const OCEAN_SETS = [
  ['Open Ocean', { sky: ['#83a4d4', '#b6fbff'], sea: ['#1c6ea4', '#0b3d6b'], sun: null }],
  ['Ocean Sunset', { sky: ['#f83600', '#f9d423'], sea: ['#c94b2b', '#5e1f16'], sun: { color: '#fff1ba', r: 0.09 } }],
  ['Deep Calm', { sky: ['#0f2027', '#203a43'], sea: ['#144552', '#06222c'], sun: { color: '#cdfff9', r: 0.05 } }],
  ['Rose Water', { sky: ['#fddb92', '#d1fdff'], sea: ['#7fb2c9', '#41708c'], sun: { color: '#fff7e0', r: 0.07 } }],
  ['Midnight Sea', { sky: ['#141e30', '#243b55'], sea: ['#1b2f4b', '#0a1424'], sun: { color: '#e8ecff', r: 0.04 } }],
];
OCEAN_SETS.forEach(([name, cfg], i) =>
  add(`ocean-${i}`, name, 'nature', (ctx, w, h) => drawOcean(ctx, w, h, cfg, mulberry32(hashSeed(`ocean-${i}`)))));

const BEACH_SETS = [
  ['Morning Beach', { sky: ['#a1c4fd', '#c2e9fb'], sea: '#4aa3c7', sand: '#f2e2c4' }],
  ['Sunset Shore', { sky: ['#ff9a9e', '#fecfef'], sea: '#c76b8e', sand: '#f6dcc0' }],
  ['Tropic Sand', { sky: ['#43cea2', '#a8e6cf'], sea: '#2c9e8a', sand: '#fdf1d6' }],
  ['Dusk Tide', { sky: ['#355c7d', '#c06c84'], sea: '#4b4a77', sand: '#d9b48f' }],
];
BEACH_SETS.forEach(([name, cfg], i) =>
  add(`beach-${i}`, name, 'nature', (ctx, w, h) => drawBeach(ctx, w, h, cfg)));

const RIVER_SETS = [
  ['River Valley', { sky: ['#89f7fe', '#66a6ff'], hills: ['#2f6b4f', '#245240'], river: ['#9ad9f0', '#3a89b8'] }],
  ['Evening River', { sky: ['#fceabb', '#f8b500'], hills: ['#6b4a2f', '#523823'], river: ['#f0cf9a', '#b8823a'] }],
  ['Emerald Bend', { sky: ['#d4fc79', '#96e6a1'], hills: ['#1f5133', '#173d27'], river: ['#b8f0d9', '#3ab883'] }],
  ['Blue Canyon', { sky: ['#e0c3fc', '#8ec5fc'], hills: ['#54418c', '#3d2e6b'], river: ['#cdd6ff', '#6a7fd6'] }],
];
RIVER_SETS.forEach(([name, cfg], i) =>
  add(`river-${i}`, name, 'nature', (ctx, w, h) => drawRiver(ctx, w, h, cfg)));

const FOREST_SETS = [
  ['Rainforest Mist', { sky: ['#dff5ec', '#a8dcc9'], canopy: ['#69b795', '#3f9270', '#256b4e', '#123f2c'] }],
  ['Deep Jungle', { sky: ['#b7e4c7', '#74c69d'], canopy: ['#40916c', '#2d6a4f', '#1b4332', '#081c15'] }],
  ['Cloud Forest', { sky: ['#e6e9f0', '#eef1f5'], canopy: ['#95a9a0', '#6d8a7d', '#4a6b5c', '#2c473b'] }],
  ['Golden Canopy', { sky: ['#f6d365', '#fda085'], canopy: ['#c98a3d', '#9c6428', '#6f4518', '#45290c'] }],
  ['Night Jungle', { sky: ['#232526', '#414345'], canopy: ['#2f4f3e', '#22392d', '#16261e', '#0b1510'] }],
];
FOREST_SETS.forEach(([name, cfg], i) =>
  add(`forest-${i}`, name, 'nature', (ctx, w, h) => drawRainforest(ctx, w, h, cfg, mulberry32(hashSeed(`forest-${i}`)))));

// Real tree-shaped woodlands (pines + round-canopy deciduous trees, layered for depth)
const WOODLAND_SETS = [
  ['Pine Grove', { sky: ['#cfe8d8', '#a9d4b8'], ground: '#5a7a4e', pine: '#2f5233', round: '#4a7a3e' }],
  ['Autumn Trees', { sky: ['#fbeec1', '#f7c59f'], ground: '#7a5a3a', pine: '#7a4a2a', round: '#c97b3d' }],
  ['Spring Woods', { sky: ['#eaf7ff', '#cfeffe'], ground: '#6a8f4e', pine: '#3f7a4a', round: '#79b558' }],
  ['Evening Grove', { sky: ['#4a5f7a', '#1c2740'], ground: '#2c3a2c', pine: '#1e3324', round: '#294a2c' }],
  ['Moonlit Pines', { sky: ['#0a0f1f', '#182640'], ground: '#131d18', pine: '#16281c', round: '#1c3324', night: true, moon: { x: 0.75, y: 0.18, r: 0.055 } }],
];
WOODLAND_SETS.forEach(([name, cfg], i) =>
  add(`woodland-${i}`, name, 'nature', (ctx, w, h) => drawForestScene(ctx, w, h, cfg, mulberry32(hashSeed(`woodland-${i}`)))));

// Waterfalls — narrow ribbon, wide curtain, and twin/braided variants
const WATERFALL_SETS = [
  ['Canyon Falls', {
    sky: ['#dff3ea', '#eef8ee'],
    ridges: ['#8fb3ad', '#4d7a76', '#1f4a48'],
    falls: { top: '#eefaff', bottom: '#a9d6e8' },
    pool: { deep: '#0e5a63', shallow: '#2ea8ad' },
    pine: '#123a30',
    mist: 'rgba(255,255,255,0.85)',
    widths: [0.05, 0.05],
  }],
  ['Wide Curtain', {
    sky: ['#bfe0e6', '#e6f4f1'],
    ridges: ['#7fa6a0', '#3d6864', '#1c3e3c'],
    falls: { top: '#f2fcff', bottom: '#b7dceb' },
    pool: { deep: '#0c4e56', shallow: '#2a9aa0' },
    pine: '#0f2e26',
    mist: 'rgba(255,255,255,0.85)',
    widths: [0.18],
  }],
  ['Emerald Gorge', {
    sky: ['#e8f5e0', '#f4faf0'],
    ridges: ['#9fbf8a', '#5a8a5e', '#2a4e34'],
    falls: { top: '#effcff', bottom: '#a3d4e5' },
    pool: { deep: '#12564a', shallow: '#3aab8e' },
    pine: '#16352a',
    mist: 'rgba(255,255,255,0.85)',
    widths: [0.06, 0.06],
  }],
  ['Golden Canyon', {
    sky: ['#ffd9a0', '#ff9e6b'],
    ridges: ['#c99a6a', '#8a5c3a', '#4a2e1c'],
    falls: { top: '#fff3d6', bottom: '#e8b878' },
    pool: { deep: '#5a3418', shallow: '#c98a3d' },
    pine: '#2e1e10',
    mist: 'rgba(255,240,220,0.8)',
    widths: [0.16],
  }],
  ['Twilight Falls', {
    sky: ['#3d5c78', '#8aa8c2'],
    ridges: ['#4a6a80', '#2c465c', '#152530'],
    falls: { top: '#dceaf5', bottom: '#8fb8d6' },
    pool: { deep: '#0f2c3d', shallow: '#245a72' },
    pine: '#0a1a18',
    mist: 'rgba(220,235,245,0.75)',
    widths: [0.06, 0.07],
  }],
];
WATERFALL_SETS.forEach(([name, cfg], i) =>
  add(`waterfall-${i}`, name, 'nature', (ctx, w, h) => drawWaterfallScene(ctx, w, h, cfg, mulberry32(hashSeed(`waterfall-${i}`)))));

// Urban & everyday-life scenes (26)
const NEIGHBORHOOD_SETS = [
  ['Sunny Street', { sky: ['#a1c4fd', '#c2e9fb'], houses: ['#e8d9c0', '#d9c7a8', '#c9b896'], roofColors: ['#8a4a3a', '#5c4a3a', '#7a5a44'], street: '#5a5a5a', sidewalk: '#8a8a8a' }],
  ['Golden Evening', { sky: ['#ffb75e', '#ed8f03'], houses: ['#c98a5a', '#b87a4a', '#a86a3e'], roofColors: ['#5c3a2a', '#4a2e20', '#3a2418'], street: '#4a4238', sidewalk: '#7a6e5c' }],
  ['Blue Dusk', { sky: ['#4a5f8a', '#8a9fc7'], houses: ['#7a8aa0', '#6a7a90', '#5a6a80'], roofColors: ['#3a3a4a', '#2c2c3a', '#20202c'], street: '#2c2c34', sidewalk: '#4a4a54', night: true, moon: { x: 0.85, y: 0.15, r: 0.045 } }],
  ['Quiet Night', { sky: ['#0a0f1f', '#182640'], houses: ['#2c3444', '#242c3a', '#1c2330'], roofColors: ['#141a24', '#10151d', '#0c1016'], street: '#0e1116', sidewalk: '#20242c', night: true, moon: { x: 0.2, y: 0.14, r: 0.05 } }],
];
NEIGHBORHOOD_SETS.forEach(([name, cfg], i) =>
  add(`neighborhood-${i}`, name, 'urban', (ctx, w, h) => drawNeighborhoodScene(ctx, w, h, cfg, mulberry32(hashSeed(`neighborhood-${i}`)))));

const CITY_STREET_SETS = [
  ['City Afternoon', { sky: ['#a1c4fd', '#cfe0f0'], buildings: ['#5a6a7a', '#4a5866', '#3d4a56'], street: '#3a3a3a' }],
  ['Downtown Dusk', { sky: ['#ff9966', '#ff5e62'], buildings: ['#3d3550', '#2e2740', '#241d33'], street: '#26222c' }],
  ['City Lights', { sky: ['#0b1026', '#1c2c4a'], buildings: ['#1a2030', '#141a28', '#0f141f'], street: '#12151c', night: true, moon: { x: 0.15, y: 0.12, r: 0.045 } }],
];
CITY_STREET_SETS.forEach(([name, cfg], i) =>
  add(`city-${i}`, name, 'urban', (ctx, w, h) => drawCityStreetScene(ctx, w, h, cfg, mulberry32(hashSeed(`city-${i}`)))));

const PARK_WALK_SETS = [
  ['Morning Walk', { sky: ['#cfe9fb', '#eef8ff'], grass: '#7a9c5e', path: '#c9b896', pine: '#3f7a4a', round: '#6ba050' }],
  ['Autumn Path', { sky: ['#fbeec1', '#f7c59f'], grass: '#8a7a4a', path: '#b89a6e', pine: '#7a4a2a', round: '#c97b3d' }],
  ['Evening Stroll', { sky: ['#b494c7', '#6a5a94'], grass: '#4a5a44', path: '#8a7a68', pine: '#2c4a2e', round: '#3d5c3a' }],
];
PARK_WALK_SETS.forEach(([name, cfg], i) =>
  add(`park-walk-${i}`, name, 'urban', (ctx, w, h) => drawParkWalkScene(ctx, w, h, cfg, mulberry32(hashSeed(`park-walk-${i}`)))));

const PARK_SIT_SETS = [
  ['Shade Tree Bench', { sky: ['#cfe9fb', '#eef8ff'], grass: '#7a9c5e', canopy: '#4a7a3e' }],
  ['Autumn Bench', { sky: ['#fbeec1', '#f7c59f'], grass: '#8a7a4a', canopy: '#c97b3d' }],
  ['Sunset Bench', { sky: ['#ff9a9e', '#fecfef'], grass: '#6a7a54', canopy: '#7a5a3e' }],
];
PARK_SIT_SETS.forEach(([name, cfg], i) =>
  add(`park-sit-${i}`, name, 'urban', (ctx, w, h) => drawParkSitScene(ctx, w, h, cfg, mulberry32(hashSeed(`park-sit-${i}`)))));

// Relationships — solid-silhouette people together (10)
add('rel-couple-walk', 'Sunset Hand in Hand', 'relationships', (ctx, w, h) =>
  drawCoupleWalkScene(ctx, w, h, { sky: ['#ff9a76', '#ffd9a0'], sea: '#c76b8e', sand: '#f2dcc0', figure: 'rgba(30,20,20,0.9)' }, mulberry32(hashSeed('rel-couple-walk'))));
add('rel-parent-child', 'Parent & Child', 'relationships', (ctx, w, h) =>
  drawParentChildWalkScene(ctx, w, h, { sky: ['#cfe9fb', '#eef8ff'], grass: '#7a9c5e', path: '#c9b896', figure: 'rgba(30,25,20,0.88)' }, mulberry32(hashSeed('rel-parent-child'))));
add('rel-family-four', 'Family of Four', 'relationships', (ctx, w, h) =>
  drawFamilyFourScene(ctx, w, h, { sky: ['#fbeec1', '#f7c59f'], grass: '#8a9c5e', figure: 'rgba(35,25,20,0.88)' }, mulberry32(hashSeed('rel-family-four'))));
add('rel-embrace', 'The Embrace', 'relationships', (ctx, w, h) =>
  drawEmbraceScene(ctx, w, h, { sky: ['#f6d365', '#fda085'], ground: '#a97c50', figure: 'rgba(30,20,15,0.9)' }, mulberry32(hashSeed('rel-embrace'))));
add('rel-friends-dock', 'Friends on the Dock', 'relationships', (ctx, w, h) =>
  drawFriendsDockScene(ctx, w, h, { sky: ['#ff9966', '#ffd9a0'], water: '#4a6a8a', dock: '#5a4230', figure: 'rgba(25,20,18,0.9)' }, mulberry32(hashSeed('rel-friends-dock'))));
add('rel-shoulder-carry', 'Shoulder Ride', 'relationships', (ctx, w, h) =>
  drawShoulderCarryScene(ctx, w, h, { sky: ['#cfe9fb', '#eef8ff'], grass: '#7a9c5e', figure: 'rgba(30,25,20,0.88)' }, mulberry32(hashSeed('rel-shoulder-carry'))));
add('rel-couple-bench', 'Sunset Bench for Two', 'relationships', (ctx, w, h) =>
  drawCoupleBenchScene(ctx, w, h, { sky: ['#fbc2eb', '#a6c1ee'], ground: '#6a7a54', figure: 'rgba(30,20,20,0.9)' }, mulberry32(hashSeed('rel-couple-bench'))));
add('rel-grandparent', 'Grandparent & Grandchild', 'relationships', (ctx, w, h) =>
  drawGrandparentScene(ctx, w, h, { sky: ['#e0eafc', '#cfdef3'], grass: '#7a9c5e', figure: 'rgba(30,25,20,0.88)' }, mulberry32(hashSeed('rel-grandparent'))));
add('rel-campfire', 'Campfire Gathering', 'relationships', (ctx, w, h) =>
  drawCampfireScene(ctx, w, h, { sky: ['#0a0f1f', '#182640'], ground: '#141a12', figure: 'rgba(10,8,8,0.92)', fire: '#ffb347', night: true, moon: { x: 0.82, y: 0.14, r: 0.045 } }, mulberry32(hashSeed('rel-campfire'))));
add('rel-wedding-arch', 'Wedding Arch', 'relationships', (ctx, w, h) =>
  drawWeddingArchScene(ctx, w, h, { sky: ['#fdeff2', '#e8d9f0'], ground: '#a8c090', arch: '#8a6a4a', figure: 'rgba(35,28,25,0.88)' }, mulberry32(hashSeed('rel-wedding-arch'))));

// Gradients (36)
const LINEAR_PALETTES = [
  ['Cerulean Deep', ['#247BA0', '#1B2845'], 120], ['Aqua Mint', ['#37B49A', '#CDFFF9'], 45],
  ['Sunset Coral', ['#ff7e5f', '#feb47b'], 90], ['Royal Plum', ['#41295a', '#2F0743'], 135],
  ['Berry Crush', ['#8E2DE2', '#4A00E0'], 60], ['Peach Glow', ['#ffecd2', '#fcb69f'], 90],
  ['Forest Dawn', ['#134E5E', '#71B280'], 120], ['Fire Sky', ['#f12711', '#f5af19'], 75],
  ['Ocean Fade', ['#2E3192', '#1BFFFF'], 45], ['Rose Gold', ['#b76e79', '#f6e2d3'], 100],
  ['Slate Night', ['#0f0c29', '#302b63'], 135], ['Lime Light', ['#a8ff78', '#78ffd6'], 60],
  ['Amber Dusk', ['#ba8b02', '#181818'], 115], ['Sky Soft', ['#a1c4fd', '#c2e9fb'], 90],
  ['Crimson Deep', ['#870000', '#190A05'], 120], ['Lavender Air', ['#e0c3fc', '#8ec5fc'], 45],
  ['Teal Steel', ['#1F4037', '#99F2C8'], 100], ['Golden Hour', ['#FFB75E', '#ED8F03'], 80],
  ['Ink Blue', ['#1e3c72', '#2a5298'], 90], ['Coral Reef', ['#ff9966', '#ff5e62'], 60],
  ['Mint Frost', ['#CDFFF9', '#A5FFD6'], 135], ['Grape Soda', ['#DA22FF', '#9733EE'], 75],
  ['Ember', ['#CB356B', '#BD3F32'], 110], ['Deep Space', ['#000428', '#004e92'], 90],
];
LINEAR_PALETTES.forEach(([name, stops, angle], i) =>
  add(`grad-${i}`, name, 'gradients', (ctx, w, h) => linear(ctx, w, h, stops, { angle })));

const RADIAL_PALETTES = [
  ['Halo Gold', ['#ffe9a8', '#e0a940', '#7a4b12']], ['Halo Cerulean', ['#9adcf5', '#247BA0', '#12283c']],
  ['Halo Rose', ['#ffd6e8', '#d16f9e', '#5c2242']], ['Halo Mint', ['#e4fff8', '#5ecfb2', '#134639']],
  ['Halo Violet', ['#e3d3ff', '#8a5fd1', '#2c1650']], ['Halo Ember', ['#ffd2a8', '#e06b3d', '#511f12']],
  ['Halo Night', ['#4a5f8a', '#243456', '#0b1223']], ['Halo Dawn', ['#fff2cf', '#f0a882', '#7a4258']],
  ['Halo Sea', ['#bdf1ff', '#4a9fc7', '#123952']], ['Halo Forest', ['#d3f2c8', '#5f9e57', '#1c3d1e']],
  ['Halo Slate', ['#c6ccd8', '#6a7488', '#232a38']], ['Halo Coral', ['#ffe0d1', '#f08a6d', '#6b2a25']],
];
RADIAL_PALETTES.forEach(([name, stops], i) =>
  add(`radial-${i}`, name, 'gradients', (ctx, w, h) => radial(ctx, w, h, stops)));

// Textures (20)
const GRAIN_BASES = [
  ['Linen Cream', ['#f5efe4', '#e8ddc8'], true], ['Linen Sage', ['#e4ece2', '#c9d8c5'], true],
  ['Linen Sky', ['#e8f0f6', '#cfe0ec'], true], ['Linen Blush', ['#f8e8e8', '#ecd0d0'], true],
  ['Linen Sand', ['#f3e8d3', '#e2cfa8'], true], ['Stone Warm', ['#b9aa97', '#94846f'], true],
  ['Stone Cool', ['#a3adb8', '#7d8894'], true], ['Slate Grain', ['#3d4451', '#252a34'], false],
  ['Charcoal Grain', ['#33322e', '#1e1d1a'], false], ['Navy Grain', ['#22304a', '#131d30'], false],
  ['Forest Grain', ['#2c4436', '#182a20'], false], ['Wine Grain', ['#4a2430', '#2c1219'], false],
  ['Espresso', ['#3b2a20', '#221610'], false], ['Indigo Grain', ['#2f2a52', '#1a1733'], false],
  ['Teal Grain', ['#1d4a4a', '#0e2b2b'], false], ['Clay', ['#b0685a', '#8a4636'], true],
  ['Fog', ['#dfe3e8', '#c4cad2'], true], ['Moss', ['#7d8f5f', '#5a6b40'], true],
  ['Dune', ['#d8b98a', '#b8945e'], true], ['Midnight Grain', ['#141a2a', '#090d18'], false],
];
GRAIN_BASES.forEach(([name, base, light], i) =>
  add(`grain-${i}`, name, 'textures', (ctx, w, h) => drawGrain(ctx, w, h, base, { light }, mulberry32(hashSeed(`grain-${i}`)))));

// Patterns (20)
add('dots-0', 'Dots Navy', 'patterns', (ctx, w, h) => drawDots(ctx, w, h, { base: ['#1B2845', '#25355c'], dot: 'rgba(255,255,255,0.16)' }));
add('dots-1', 'Dots Cream', 'patterns', (ctx, w, h) => drawDots(ctx, w, h, { base: ['#f6f0e4', '#eadfc8'], dot: 'rgba(120,100,60,0.22)' }));
add('dots-2', 'Dots Teal', 'patterns', (ctx, w, h) => drawDots(ctx, w, h, { base: ['#0e3a35', '#155247'], dot: 'rgba(165,255,214,0.2)' }));
add('dots-3', 'Dots Plum', 'patterns', (ctx, w, h) => drawDots(ctx, w, h, { base: ['#3b1f47', '#552d63'], dot: 'rgba(255,255,255,0.14)' }));
add('diag-0', 'Stripes Cerulean', 'patterns', (ctx, w, h) => drawDiagonals(ctx, w, h, { base: ['#1d6484', '#247BA0'], line: 'rgba(255,255,255,0.1)' }));
add('diag-1', 'Stripes Sand', 'patterns', (ctx, w, h) => drawDiagonals(ctx, w, h, { base: ['#e8d5ae', '#dbc290'], line: 'rgba(120,90,40,0.15)' }));
add('diag-2', 'Stripes Night', 'patterns', (ctx, w, h) => drawDiagonals(ctx, w, h, { base: ['#141a2a', '#1d2740'], line: 'rgba(255,255,255,0.07)' }));
add('diag-3', 'Stripes Rose', 'patterns', (ctx, w, h) => drawDiagonals(ctx, w, h, { base: ['#b95c7a', '#a04a68'], line: 'rgba(255,255,255,0.12)' }));
add('waves-0', 'Waves Deep', 'patterns', (ctx, w, h) => drawWaves(ctx, w, h, { base: ['#0b2540', '#123a5c'], line: 'rgba(140,200,255,0.22)' }));
add('waves-1', 'Waves Mint', 'patterns', (ctx, w, h) => drawWaves(ctx, w, h, { base: ['#0f4a3e', '#17685a'], line: 'rgba(205,255,249,0.25)' }));
add('waves-2', 'Waves Dawn', 'patterns', (ctx, w, h) => drawWaves(ctx, w, h, { base: ['#f7d9c4', '#f2bfa0'], line: 'rgba(160,90,60,0.25)' }));
add('waves-3', 'Waves Slate', 'patterns', (ctx, w, h) => drawWaves(ctx, w, h, { base: ['#2b303b', '#3a4150'], line: 'rgba(255,255,255,0.12)' }));
add('rings-0', 'Rings Gold', 'patterns', (ctx, w, h) => drawRings(ctx, w, h, { base: ['#2c2313', '#4a3a1c'], ring: 'rgba(240,200,120,0.3)' }));
add('rings-1', 'Rings Ice', 'patterns', (ctx, w, h) => drawRings(ctx, w, h, { base: ['#dfeaf2', '#c3d8e8'], ring: 'rgba(60,110,150,0.25)' }));
add('rings-2', 'Rings Violet', 'patterns', (ctx, w, h) => drawRings(ctx, w, h, { base: ['#241a3d', '#382a5c'], ring: 'rgba(200,170,255,0.25)' }));
add('rings-3', 'Rings Sea', 'patterns', (ctx, w, h) => drawRings(ctx, w, h, { base: ['#0e2f3d', '#174a5e'], ring: 'rgba(150,230,240,0.25)' }));
add('rays-0', 'Rays Dawn', 'patterns', (ctx, w, h) => drawRays(ctx, w, h, { base: ['#ffd194', '#f5a26b'], ray: 'rgba(255,255,255,0.18)' }));
add('rays-1', 'Rays Cerulean', 'patterns', (ctx, w, h) => drawRays(ctx, w, h, { base: ['#1d6484', '#12405a'], ray: 'rgba(255,255,255,0.08)' }));
add('rays-2', 'Rays Rose', 'patterns', (ctx, w, h) => drawRays(ctx, w, h, { base: ['#c9647f', '#94405c'], ray: 'rgba(255,255,255,0.1)' }));
add('rays-3', 'Rays Night', 'patterns', (ctx, w, h) => drawRays(ctx, w, h, { base: ['#10182c', '#1c2946'], ray: 'rgba(160,190,255,0.08)' }));

export const SHARE_BACKGROUNDS = defs;

export const BACKGROUND_CATEGORIES = [
  { key: 'nature', label: 'Nature' },
  { key: 'urban', label: 'Everyday Life' },
  { key: 'relationships', label: 'Relationships' },
  { key: 'gradients', label: 'Gradients' },
  { key: 'textures', label: 'Textures' },
  { key: 'patterns', label: 'Patterns' },
];

export function getBackground(id) {
  return defs.find(b => b.id === id) || defs[0];
}
